import { useState } from "react";
import { toast } from "sonner";
import { Plus, ArrowUp, ArrowDown, Eye, EyeOff, ImagePlus, Loader2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Field, Btn, DeleteBtn, Loading, Empty, useCrud, handleErr } from "./AdminUI";

async function uploadPhoto(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await endpoints.uploadImage(fd);
  return data.url;
}

const BLANK = { name: "", role: "", photo: "", order: 0, isActive: true };

export default function AdminJournalists() {
  const { data: list, loading, reload } = useCrud(endpoints.adminJournalists);
  const [form, setForm] = useState(BLANK);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const pick = async (file, setter, obj) => {
    if (!file) return;
    setUploading(true);
    try { const url = await uploadPhoto(file); setter({ ...obj, photo: url }); toast.success("Photo uploaded"); }
    catch (e) { handleErr(e); } finally { setUploading(false); }
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try { await endpoints.adminCreateJournalist({ ...form, order: parseInt(form.order || 0, 10) });
      toast.success("Journalist added"); setForm(BLANK); setShowForm(false); reload();
    } catch (e) { handleErr(e); } finally { setSaving(false); }
  };
  const patch = async (j, body) => { try { await endpoints.adminUpdateJournalist(j.id, body); reload(); } catch (e) { handleErr(e); } };
  const del = async (j) => { if (!window.confirm(`Remove ${j.name}?`)) return; try { await endpoints.adminDeleteJournalist(j.id); toast.success("Removed"); reload(); } catch (e) { handleErr(e); } };
  const move = async (j, dir) => { await patch(j, { order: (j.order || 0) + dir }); };
  const editPhoto = async (j, file) => { if (!file) return; try { const url = await uploadPhoto(file); await patch(j, { photo: url }); toast.success("Photo updated"); } catch (e) { handleErr(e); } };

  return (
    <div>
      <PageHeader title="Our Journalists" desc="Add, edit, reorder, activate and manage journalist photos shown on the homepage.">
        <Btn onClick={() => setShowForm((s) => !s)} data-testid="new-journalist-toggle"><Plus className="w-4 h-4" /> Add journalist</Btn>
      </PageHeader>

      {showForm && (
        <Card className="p-5 mb-6 space-y-3" data-testid="journalist-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="journalist-name" />
            <Field label="Role / title" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
            <Field label="Order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
          </div>
          <div className="flex items-center gap-3">
            <label className="h-11 px-4 rounded-xl border border-[#273042] bg-[#0A0C10] text-slate-300 text-sm cursor-pointer flex items-center gap-2">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Upload photo
              <input type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files?.[0], setForm, form)} data-testid="journalist-photo" />
            </label>
            {form.photo && <img src={form.photo} alt="preview" className="w-12 h-12 rounded-lg object-cover" />}
          </div>
          <div className="flex gap-3"><Btn onClick={create} disabled={saving} data-testid="journalist-create">{saving ? "Saving…" : "Add journalist"}</Btn><Btn variant="ghost" onClick={() => { setShowForm(false); setForm(BLANK); }}>Cancel</Btn></div>
        </Card>
      )}

      {loading ? <Loading /> : (list || []).length === 0 ? <Empty text="No journalists yet." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((j) => (
            <Card key={j.id} className="p-4 flex items-center gap-3" data-testid={`admin-journalist-${j.id}`}>
              <label className="relative shrink-0 cursor-pointer">
                <img src={j.photo || "/bb-logo.png"} alt={j.name} className={`w-16 h-16 rounded-xl object-cover ${j.isActive === false ? "opacity-40" : ""}`} onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
                <input type="file" accept="image/*" className="hidden" onChange={(e) => editPhoto(j, e.target.files?.[0])} />
              </label>
              <div className="min-w-0 flex-1">
                <input defaultValue={j.name} onBlur={(e) => e.target.value !== j.name && patch(j, { name: e.target.value })} className="w-full bg-transparent text-white font-semibold text-sm outline-none" />
                <input defaultValue={j.role} placeholder="Role" onBlur={(e) => e.target.value !== j.role && patch(j, { role: e.target.value })} className="w-full bg-transparent text-[#E11D2A] text-xs outline-none" />
                <div className="flex items-center gap-1 mt-1.5">
                  <button onClick={() => move(j, -1)} className="w-7 h-7 rounded bg-white/5 text-slate-300 hover:text-white flex items-center justify-center"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => move(j, 1)} className="w-7 h-7 rounded bg-white/5 text-slate-300 hover:text-white flex items-center justify-center"><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button onClick={() => patch(j, { isActive: j.isActive === false })} data-testid={`journalist-toggle-${j.id}`} className="w-7 h-7 rounded bg-white/5 text-slate-300 hover:text-white flex items-center justify-center">{j.isActive === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
                  <DeleteBtn onClick={() => del(j)} testid={`journalist-del-${j.id}`} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
